import React, { useEffect, useState } from 'react';
import {
    Autocomplete, Box, Button, Card, CardContent, CircularProgress, Grid, TextField, Typography,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { AuthService } from '../../../services/auth.service';
import { useToast } from '../../../context/ToastContext';
import { CommercialReportService } from '../../../services/commercialReport.service';
import { BuyerOrderService } from '../../../services/buyerOrder.service';
import { IOrderProfitabilityReport } from '../../../interfaces/commercial.interface';
import { IBuyerOrder } from '../../../interfaces/garments/buyerOrder.interface';
import { formatMoney } from '../../../utils/format';
import { triggerDownload } from '../../statutoryRegisters/shared';

const money = (n: number | null | undefined): string => formatMoney(n, 'BDT');

const percent = (n: number | null | undefined): string =>
    n === null || n === undefined ? '—' : `${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

const COST_BASIS_LABELS: Record<string, string> = {
    RECEIVED: 'Actual cost (receipts)',
    COMMITTED: 'Actual cost (POs)',
    PLANNED: 'Planned cost (cost sheet)',
    RECEIVED_PLUS_PLANNED: 'Receipts + planned cost for unreceived materials',
    RECEIVED_PLUS_COMMITTED: 'Receipts + committed POs for unreceived materials',
};

interface StatProps { label: string; value: string; }
const Stat: React.FC<StatProps> = ({ label, value }) => (
    <Grid size={{ xs: 6, sm: 4, md: 3 }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="h6">{value}</Typography>
    </Grid>
);

/**
 * Order profitability report -- revenue vs cost for one buyer order. The operator picks the order
 * and enters the exchange rate (BDT per 1 unit of the order's currency; not needed for BDT orders).
 * Cost is the actual PO/receipt cost once purchase orders cover at least 80% of the style cost
 * sheet's planned cost; until then the plan (converted with the same rate) stands in. The card says
 * which basis was used.
 */
const OrderProfitabilityReport: React.FC = () => {
    const toast = useToast();
    const institutionId = AuthService.getSelectedInstitution()!;
    const [orders, setOrders] = useState<IBuyerOrder[]>([]);
    const [ordersLoading, setOrdersLoading] = useState(true);
    const [selectedOrder, setSelectedOrder] = useState<IBuyerOrder | null>(null);
    const [exchangeRate, setExchangeRate] = useState<string>('');
    const [report, setReport] = useState<IOrderProfitabilityReport | null>(null);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                setOrdersLoading(true);
                const res = await BuyerOrderService.list(institutionId, 0, 200);
                setOrders(res.content);
            } catch (e) {
                toast.showApiError(e, 'Failed to load buyer orders');
            } finally {
                setOrdersLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId]);

    const parsedRate = (): number | undefined => {
        const n = Number(exchangeRate);
        return exchangeRate.trim() !== '' && Number.isFinite(n) && n > 0 ? n : undefined;
    };

    // The order's own currency, when known: the selected order's field before a report has loaded,
    // else the report's echoed field. Falls back to a generic label so the field always reads sensibly.
    const orderCurrencyLabel = selectedOrder?.currencyCode || report?.orderCurrencyCode || 'order currency';
    const exchangeRateLabel = `Exchange rate (BDT per 1 ${orderCurrencyLabel})`;
    const rateIsBlank = exchangeRate.trim() === '';
    const parsedRateValue = Number(exchangeRate);
    const rateLooksInverted = !rateIsBlank && Number.isFinite(parsedRateValue) && parsedRateValue > 0
        && parsedRateValue < 1;
    const exchangeRateHelperText = rateIsBlank
        ? 'Enter the exchange rate to see the report.'
        : rateLooksInverted
            ? 'Rates below 1 usually mean the direction is inverted.'
            : `e.g. 122 means 1 ${orderCurrencyLabel} = 122 BDT`;

    const load = async (order: IBuyerOrder, rateOverride?: number) => {
        setLoading(true);
        try {
            const data = await CommercialReportService.getOrderProfitability(institutionId, order.id, rateOverride ?? parsedRate());
            setReport(data);
        } catch (e) {
            setReport(null);
            toast.showApiError(e, 'Failed to load order profitability report');
        } finally {
            setLoading(false);
        }
    };

    const handleOrderChange = (order: IBuyerOrder | null) => {
        setSelectedOrder(order);
        setReport(null);
        if (order && order.currencyCode === 'BDT') {
            // A BDT order needs no conversion, so the rate is known.
            setExchangeRate('1');
            load(order, 1);
        }
    };

    const canFetch = !!selectedOrder && !rateIsBlank && parsedRate() !== undefined;

    const handleFetch = () => {
        if (selectedOrder && canFetch) load(selectedOrder);
    };

    const handleDownload = async () => {
        if (!selectedOrder) return;
        setDownloading(true);
        try {
            const blob = await CommercialReportService.downloadOrderProfitabilityXlsx(institutionId, selectedOrder.id, parsedRate());
            triggerDownload(blob, `order-profitability-${selectedOrder.orderNumber}.xlsx`);
        } catch (e) {
            toast.showApiError(e, 'Download failed');
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Box data-help-page="commercialReports.orderProfit.page">
            <Typography variant="h5" sx={{ mb: 2 }}>Order Profitability</Typography>
            <Box data-help-section="commercialReports.orderProfit.filters" display="flex" gap={2} alignItems="flex-start" mb={2} flexWrap="wrap">
                <Autocomplete
                    data-help="commercialReports.orderProfit.order"
                    sx={{ flex: '1 1 260px', maxWidth: 380 }}
                    size="small"
                    options={orders}
                    loading={ordersLoading}
                    value={selectedOrder}
                    getOptionLabel={(o) => o.orderNumber}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    onChange={(_, value) => handleOrderChange(value)}
                    renderInput={(params) => <TextField {...params} label="Buyer Order" placeholder="Select a buyer order" />}
                />
                <TextField
                    data-help="commercialReports.orderProfit.rate"
                    label={exchangeRateLabel}
                    type="number"
                    size="small"
                    required
                    sx={{ flex: '1 1 240px', maxWidth: 320 }}
                    value={exchangeRate}
                    error={rateIsBlank}
                    helperText={exchangeRateHelperText}
                    FormHelperTextProps={rateLooksInverted ? { sx: { color: 'warning.main' } } : undefined}
                    slotProps={{ htmlInput: { 'data-testid': 'order-profitability-rate-input' } }}
                    onChange={e => setExchangeRate(e.target.value)}
                />
                <Button
                    data-help="commercialReports.orderProfit.fetch"
                    variant="outlined" size="small"
                    onClick={handleFetch} disabled={!canFetch || loading}
                >
                    Fetch
                </Button>
                <Button
                    data-help="commercialReports.orderProfit.download"
                    variant="contained" size="small" startIcon={<FileDownloadIcon />}
                    onClick={handleDownload} disabled={!report || downloading || !canFetch}
                >
                    Export XLSX
                </Button>
            </Box>

            {loading && <CircularProgress size={24} />}

            {!selectedOrder && !loading && (
                <Typography color="text.secondary">Select a buyer order to view its profitability.</Typography>
            )}

            {!loading && selectedOrder && report && (
                <Card data-testid="order-profitability-card" data-help-section="commercialReports.orderProfit.result">
                    <CardContent>
                        <Box display="flex" gap={3} alignItems="baseline" mb={2} flexWrap="wrap">
                            <Typography variant="body2">
                                Currency: <strong data-testid="order-profitability-currency">{report.orderCurrencyCode}</strong>
                            </Typography>
                            <Typography variant="body2">
                                Exchange Rate Applied: <strong data-testid="order-profitability-rate">{report.exchangeRateApplied}</strong>
                            </Typography>
                            <Typography variant="body2">
                                Total Quantity: <strong>{report.totalQuantity}</strong>
                            </Typography>
                            <Typography variant="body2">
                                Cost used: <strong data-testid="order-profitability-basis">{COST_BASIS_LABELS[report.headlineCostBasis] ?? report.headlineCostBasis}</strong>
                            </Typography>
                        </Box>

                        <Grid container spacing={2}>
                            <Stat label="Revenue (BDT)" value={money(report.revenueBase)} />
                            <Stat label="Committed cost (POs)" value={money(report.committedCost)} />
                            <Stat label="Received cost (receipts)" value={money(report.receivedCost)} />
                            <Stat
                                label={`Planned cost (cost sheet, ${report.orderCurrencyCode})`}
                                value={formatMoney(report.plannedCmCost, report.orderCurrencyCode)}
                            />
                            <Stat label="Planned cost (BDT)" value={money(report.plannedCostBase)} />
                            <Stat label="Cost used in margin (BDT)" value={money(report.headlineCost)} />
                            {report.unreceivedCost > 0 && report.receivedCost > 0 && (
                                <Stat label="of which not yet received (BDT)" value={money(report.unreceivedCost)} />
                            )}
                            <Stat label="Gross margin (BDT)" value={money(report.grossMargin)} />
                            <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                                <Typography variant="caption" color="text.secondary">Margin %</Typography>
                                <Typography variant="h6" data-testid="order-profitability-margin-percent">
                                    {percent(report.marginPercent)}
                                </Typography>
                            </Grid>
                        </Grid>
                        {report.headlineCostBasis === 'PLANNED' && (
                            <Typography variant="caption" color="text.secondary" display="block" mt={2}>
                                No purchase orders or receipts are linked to this order yet, so the margin uses the
                                planned cost from the style cost sheet, converted at the rate entered.
                            </Typography>
                        )}
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

export default OrderProfitabilityReport;
